import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AIFeature, AIFeatureParentPreset, AIFeaturePreset, ComplexAIResultType, RoomSession } from '@zocks-communications/client-sdk-js'
import {
  AIResultDisplayModel,
  AIResultViewModel,
  mapAiResultsToAiResultViewModel,
  SegmentedResultViewModel,
  StructuredSummaryResultViewModel,
} from './AIResultMapper'
import { Form, OverlayTrigger, Toast, ToastContainer, Tooltip } from 'react-bootstrap'
import { ActionableAIFeature, EmailActionAIResult, sortAIFeaturesByMetadataOrder } from './AIResultsViews'
import { CopyActionAIResult, createCopyFeatureResults } from './CopyActionAIResult'
import { AIExecutionType } from './models'
import { useTracking, AIResultMetadataType, TrackEvent } from './common/tracking'
import { getRoomTrackingDetails } from './tracking'
import { useAPIClient } from './APIClientProvider'
import { Button } from '../../../components/Button/Button'
import { AiFeature } from '../../../models/AiFeature'
import { AiFeaturePreset } from '../../../models/AiFeaturePreset'
import { AiFeatureResult } from '../../../models/AiResult'

export const getTotalSegmentations = (aiResult: AIResultViewModel) => {
  return aiResult.result && aiResult.result.type === 'segmented'
    ? Math.max(
        ...aiResult.result.segmentResults.map((segmentResult) => {
          return segmentResult.metadata.segmentIdx
        }),
      ) + 1
    : 0
}

export const copyAllAIFeatureResults = async (aiFeatures: AIFeature[], aiResults: AIResultViewModel[], session: RoomSession) => {
  const copyableFeatureIds = aiFeatures
    .filter((aiFeature) => {
      try {
        const metadata = JSON.parse(aiFeature.metadata)
        return metadata.actionType === 'copy'
      } catch (e) {}
      return false
    })
    .sort(sortAIFeaturesByMetadataOrder)
  await navigator.clipboard.writeText(
    copyableFeatureIds
      .map((aiFeature) => {
        const aiFeatureResults = aiResults.filter((result) => result.aiFeatureId === aiFeature.id)
        if (aiFeatureResults.length) {
          const aiFeatureResultCopyText = createCopyFeatureResults(aiFeatureResults, session.startedAt)
          if (aiFeatureResultCopyText) {
            return `${aiFeature.name}\n\n${aiFeatureResultCopyText}`
          }
          return ''
        }
        return ''
      })
      .filter((copyText) => copyText)
      .join('\n\n'),
  )
}

export type UpdateValue = boolean

export type UpdatableProperties = {
  [key in 'dismissed' | 'selected']: UpdateValue
}

export const updateAIResultsProperties = ({
  aiResult,
  aiResults,
  propertyToUpdate,
  propertyValue,
}: {
  aiResult?: AIResultDisplayModel
  aiResults: AIResultViewModel[]
  propertyToUpdate: keyof UpdatableProperties
  propertyValue?: UpdateValue
}) => {
  const updateByDisplayId = (
    result: AIResultDisplayModel,
    updateValue: UpdateValue,
    propertyToUpdate: keyof UpdatableProperties,
    cascade: boolean = false,
  ): AIResultDisplayModel => {
    if (result.displayId === aiResult?.displayId && !cascade) {
      result[propertyToUpdate] = propertyValue ?? !result[propertyToUpdate]
      return updateByDisplayId(result, result[propertyToUpdate], propertyToUpdate, true)
    }

    if (typeof propertyValue !== 'undefined') {
      result[propertyToUpdate] = propertyValue
    }

    if (cascade) {
      result[propertyToUpdate] = updateValue
    }

    if (typeof result.result === 'object' && result.result !== null) {
      const newResult = Object.entries(result.result).reduce<{ [key: string]: any }>((acc, [key, value]) => {
        if (Array.isArray(value)) {
          // If value is an array, update each element
          acc[key] = value.map((v) => updateByDisplayId(v, result[propertyToUpdate], propertyToUpdate, cascade))
        } else {
          // If value is not an array, update it directly
          acc[key] = updateByDisplayId(value, result[propertyToUpdate], propertyToUpdate, cascade)
        }
        return acc
      }, {})

      result.result = newResult
    }
    return result
  }

  return [...aiResults].map((result) => {
    if (result.result && result.result.type === 'segmented') {
      const segmentedResults = result.result as SegmentedResultViewModel
      const newSegmentedResult = segmentedResults.segmentResults.map((segmentResult) => {
        const structuredSummaryResults = segmentResult.results as StructuredSummaryResultViewModel[]
        const newResults = structuredSummaryResults.map((structuredSummaryResult) => {
          const displayModels = structuredSummaryResult.result.map((r) => {
            return updateByDisplayId(r, propertyValue ?? r[propertyToUpdate], propertyToUpdate)
          })
          return { type: 'structuredSummary', result: displayModels } as StructuredSummaryResultViewModel
        })

        return { ...segmentResult, results: newResults }
      })
      return { ...result, result: { type: ComplexAIResultType.SEGMENTED, segmentResults: newSegmentedResult } }
    }
    if (result.result && result.result.type === 'structuredSummary') {
      const structuredSummaryResult = result.result as StructuredSummaryResultViewModel
      const newResult = structuredSummaryResult.result.map((r) => {
        return updateByDisplayId(r, propertyValue ?? r[propertyToUpdate], propertyToUpdate)
      })
      return { ...result, result: { ...structuredSummaryResult, result: newResult } }
    }
    return { ...result }
  })
}

export const OndemandAIExecution: React.FC<{
  roomId?: string
  session?: RoomSession
}> = ({ roomId, session }) => {
  const { tracked } = useTracking()

  const [onDemandAiFeatures, setOnDemandAiFeatures] = useState<AIFeature[]>([])
  const [onDemandAiFeaturePresets, setOnDemandAiFeaturePresets] = useState<AIFeaturePreset[]>([])
  const [aiExecuteLoading, setAiExecuteLoading] = useState(false)
  const [selectedOndemandAiFeatureIds, setSelectedOndemandAiFeatureIds] = useState<string[]>([])
  const [aiExecuteResults, setAiExecuteResults] = useState<AIResultViewModel[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [showCopyToast, setShowCopyToast] = useState<boolean>(false)
  const [copyBlockOpened, setCopyBlockOpened] = useState<boolean>(false)
  const [emailBlockOpened, setEmailBlockOpened] = useState<boolean>(false)
  const { aiClient, aiExecuteClient } = useAPIClient()

  useEffect(() => {
    aiClient.listAIFeatures().then((result) => {
      const filteredResult = result
        .filter((aiFeature) => {
          try {
            const { onDemand, dynamic } = JSON.parse(aiFeature.metadata)
            return onDemand && !dynamic
          } catch (e) {}
          return false
        })
        .sort(sortAIFeaturesByMetadataOrder)
      setOnDemandAiFeatures(filteredResult)
    })
  }, [aiClient])

  useEffect(() => {
    Promise.all(
      selectedOndemandAiFeatureIds
        .map((aiFeatureId) => onDemandAiFeatures.find((aiFeature) => aiFeature.id === aiFeatureId))
        .filter((aiFeature) => aiFeature && aiFeature.preset)
        .map((aiFeature) => aiClient.getAIFeaturePreset((aiFeature?.preset as AIFeatureParentPreset).id)),
    ).then((aiFeaturePresets) => setOnDemandAiFeaturePresets(aiFeaturePresets))
  }, [aiClient, onDemandAiFeatures, selectedOndemandAiFeatureIds])

  const _handleExecuteAiFeatures = async () => {
    if (!roomId || !session?.id) {
      return
    }
    try {
      setAiExecuteLoading(true)
      if (!selectedOndemandAiFeatureIds.length) {
        return
      }
      try {
        const results = await aiExecuteClient.executeAIFeatures(roomId, session.id, {
          aiFeatureIds: selectedOndemandAiFeatureIds,
        })

        const selectedOndemandAiFeatures = selectedOndemandAiFeatureIds
          .map((aiFeatureId) => onDemandAiFeatures.find((aiFeature) => aiFeature.id === aiFeatureId))
          .filter((aiFeature) => aiFeature) as AIFeature[]

        const sessionId = session.id
        const viewModels = mapAiResultsToAiResultViewModel(
          results.map((aiExecuteResult, idx) => {
            const createdAt = new Date().toISOString()
            return {
              aiFeatureId: aiExecuteResult.aiFeatureId,
              inputParametersHierarchyId: null,
              result: aiExecuteResult.result as AiFeatureResult,
              id: `on-demand-result-${idx}`,
              dismissed: false,
              displayId: idx.toString(),
              roomId,
              sessionId,
              userId: aiExecuteResult.userId ?? null,
              status: 'full_context',
              createdAt,
              scores: [],
              syncStatus: [],
            }
          }),
          selectedOndemandAiFeatures as AiFeature[],
          onDemandAiFeaturePresets as AiFeaturePreset[],
        )
        setAiExecuteResults(viewModels)
      } catch (e: any) {
        if ('errors' in e) {
          setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
        } else {
          setErrorMessage(JSON.stringify(e))
        }
      }
    } finally {
      setAiExecuteLoading(false)
    }
  }
  const handleExecuteAiFeatures = tracked(_handleExecuteAiFeatures, {
    eventName: TrackEvent.ON_DEMAND_AI_FEATURES_EXECUTED,
    eventDetails: {
      ...getRoomTrackingDetails(session),
      aiFeatureIds: selectedOndemandAiFeatureIds,
      presetIds: [
        onDemandAiFeatures
          .filter((feature) => selectedOndemandAiFeatureIds.includes(feature.id) && feature.preset)
          .map((feature) => feature.preset?.id ?? undefined),
      ],
    },
  })

  const onDismissAIExecuteResult = (aiResult: AIResultDisplayModel) => {
    const results = updateAIResultsProperties({ aiResult, aiResults: aiExecuteResults, propertyToUpdate: 'dismissed' })
    setAiExecuteResults(results)
  }

  const onSelectAIExecuteResult = (aiResult: AIResultDisplayModel) => {
    const results = updateAIResultsProperties({ aiResult, aiResults: aiExecuteResults, propertyToUpdate: 'selected' })
    setAiExecuteResults(results)
  }

  const executeTooltip = useMemo(() => {
    return selectedOndemandAiFeatureIds.length ? <></> : <Tooltip id='execute-tooltip'>Please select at least one Intelligence Feature.</Tooltip>
  }, [selectedOndemandAiFeatureIds])

  const selectOnDemandAiFeature = (aiFeatureId: string) => {
    if (selectedOndemandAiFeatureIds.includes(aiFeatureId)) {
      setSelectedOndemandAiFeatureIds((selectedOndemandAiFeatureIds) => selectedOndemandAiFeatureIds.filter((id) => id !== aiFeatureId))
    } else {
      setSelectedOndemandAiFeatureIds([...selectedOndemandAiFeatureIds, aiFeatureId])
    }
  }

  const onCopyAllAIFeatureResults = tracked(
    async () => {
      await copyAllAIFeatureResults(onDemandAiFeatures, aiExecuteResults, session!)
      setShowCopyToast(true)
    },
    {
      eventName: TrackEvent.AI_RESULT_COPIED,
      eventDetails: { ...getRoomTrackingDetails(session), actionMethod: 'all', executionType: AIExecutionType.ON_DEMAND },
    },
  )

  const onToggleCopyBlockOpened = tracked(
    () => setCopyBlockOpened((opened) => !opened),
    {
      eventName: TrackEvent.AI_RESULT_OPENED,
      eventDetails: {
        ...getRoomTrackingDetails(session),
        opened: 'all',
        type: AIResultMetadataType.COPY,
        executionType: AIExecutionType.ON_DEMAND,
      },
    },
    { condition: !emailBlockOpened },
  )

  const onToggleEmailBlockOpened = tracked(
    () => setEmailBlockOpened((opened) => !opened),
    {
      eventName: TrackEvent.AI_RESULT_OPENED,
      eventDetails: {
        ...getRoomTrackingDetails(session),
        openMethod: 'all',
        type: AIResultMetadataType.EMAIL,
        executionType: AIExecutionType.ON_DEMAND,
      },
    },
    { condition: !emailBlockOpened },
  )

  const shouldShowAllActionButton = useCallback(
    (type: 'email' | 'copy') => {
      const aiFeaturesIds = onDemandAiFeatures
        .filter((aiFeature) => {
          try {
            const metadata = JSON.parse(aiFeature.metadata)
            return metadata.onDemand && metadata.actionType === type
          } catch (e) {}
          return false
        })
        .map((aiFeature) => aiFeature.id)
      return aiExecuteResults.map((result) => result.aiFeatureId).some((aiFeatureId) => aiFeaturesIds.includes(aiFeatureId))
    },
    [aiExecuteResults, onDemandAiFeatures, selectedOndemandAiFeatureIds],
  )

  const errorToast = (
    <ToastContainer className='p-3' position={'top-end'} style={{ zIndex: 1 }}>
      <Toast show={!!errorMessage} onClose={() => setErrorMessage(null)} bg='danger'>
        <Toast.Header className='ondemand-error-toast'>
          <span>On-demand Execution Error</span>
        </Toast.Header>
        <Toast.Body>{'Error during on-demand intelligence execution: ' + errorMessage}</Toast.Body>
      </Toast>
    </ToastContainer>
  )

  return (
    <>
      <ToastContainer position='top-center' className='p-3 summary-toast'>
        <Toast onClose={() => setShowCopyToast(false)} show={showCopyToast} delay={1500} autohide>
          <Toast.Body>Copied to clipboard!</Toast.Body>
        </Toast>
      </ToastContainer>
      <label>On-demand Intelligence Features</label>
      <div className='financial-advisor-ondemand'>
        {errorToast}

        <div className='financial-advisor-ondemand-selector'>
          <div className='select-wrapper'>
            {onDemandAiFeatures.map((aiFeature) => {
              return (
                <Form.Check
                  key={aiFeature.id}
                  type='checkbox'
                  id={aiFeature.id}
                  label={aiFeature.name}
                  checked={selectedOndemandAiFeatureIds.includes(aiFeature.id)}
                  onChange={() => selectOnDemandAiFeature(aiFeature.id)}
                />
              )
            })}
          </div>
          <div className='ondemand-controls'>
            <OverlayTrigger overlay={executeTooltip} placement='right'>
              <span className='ondemand-execute-wrapper'>
                <Button
                  variant='primary'
                  className='ondemand-execute-button'
                  onClick={handleExecuteAiFeatures}
                  disabled={aiExecuteLoading || !selectedOndemandAiFeatureIds.length}
                >
                  Execute Features
                </Button>
              </span>
            </OverlayTrigger>
          </div>
        </div>

        <div className='financial-advisor-ondemand-actions'>
          {session && (
            <div className='d-flex gap-3'>
              {shouldShowAllActionButton('copy') && (
                <div className='flex-1 d-flex justify-content-between w-50'>
                  <div className='ai-feature-copy-all-action'>
                    <div className='cursor-pointer' onClick={onCopyAllAIFeatureResults}>
                      <i className='bi bi-copy' /> Copy all results to clipboard
                    </div>
                  </div>
                  <div className='ai-feature-toggle-all-action'>
                    <div className='cursor-pointer' onClick={onToggleCopyBlockOpened}>
                      {copyBlockOpened ? 'Close All' : 'Open All'}
                    </div>
                  </div>
                </div>
              )}
              {shouldShowAllActionButton('email') && (
                <div className='flex-1 d-flex justify-content-end'>
                  <div className='ai-feature-toggle-all-action'>
                    <div className='cursor-pointer' onClick={onToggleEmailBlockOpened}>
                      {emailBlockOpened ? 'Close All' : 'Open All'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {aiExecuteLoading ? (
            <div className='skeleton ' style={{ width: '100%', height: '116px' }} />
          ) : aiExecuteResults.length ? (
            <div className='d-flex gap-3'>
              <div className='financial-advisor-ondemand-copy-action-wrapper'>
                {onDemandAiFeatures
                  .filter((aiFeature) => aiExecuteResults.some((r) => r.aiFeatureId === aiFeature.id))
                  .filter((aiFeature) => {
                    try {
                      return JSON.parse(aiFeature.metadata).actionType === 'copy'
                    } catch (e) {}
                    return false
                  })
                  .map((aiFeature) => {
                    return (
                      <ActionableAIFeature
                        key={aiFeature.id}
                        aiFeature={aiFeature}
                        aiResults={aiExecuteResults.filter((aiResult) => aiResult.aiFeatureId === aiFeature.id)}
                        aiResultsLoading={aiExecuteLoading}
                        ActionableAIResultElement={CopyActionAIResult}
                        onDismissAIResult={onDismissAIExecuteResult}
                        onRerunAIFeature={() => {}}
                        showScoreAIResult={false}
                        resultBlockOpened={copyBlockOpened}
                        executionType={AIExecutionType.ON_DEMAND}
                        onSelectAIResult={onSelectAIExecuteResult}
                      />
                    )
                  })}
              </div>
              <div className='financial-advisor-ondemand-email-action-wrapper'>
                {onDemandAiFeatures
                  .filter((aiFeature) => aiExecuteResults.some((r) => r.aiFeatureId === aiFeature.id))
                  .filter((aiFeature) => {
                    try {
                      return JSON.parse(aiFeature.metadata).actionType === 'email'
                    } catch (e) {}
                    return false
                  })
                  .map((aiFeature) => {
                    return (
                      <ActionableAIFeature
                        key={aiFeature.id}
                        aiFeature={aiFeature}
                        aiResults={aiExecuteResults.filter((aiResult) => aiResult.aiFeatureId === aiFeature.id)}
                        aiResultsLoading={aiExecuteLoading}
                        ActionableAIResultElement={EmailActionAIResult}
                        onDismissAIResult={onDismissAIExecuteResult}
                        onRerunAIFeature={() => {}}
                        showScoreAIResult={false}
                        resultBlockOpened={emailBlockOpened}
                        executionType={AIExecutionType.ON_DEMAND}
                        onSelectAIResult={onSelectAIExecuteResult}
                      />
                    )
                  })}
              </div>
            </div>
          ) : (
            <div>No intelligence results.</div>
          )}
        </div>
      </div>
    </>
  )
}
